import {
	Autocomplete,
	Checkbox,
	FormControlLabel,
	Stack,
	TextField,
	Typography,
	Card,
	CardContent,
	CardHeader,
	CardActions,
	IconButton,
	Divider,
	Box,
} from "@mui/material";
import { FunctionComponent } from "react";
import { HubConnection, HubConnectionState } from "@microsoft/signalr";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import { FeatureUpdateMessage } from "Infrastructure/Models/Messages/Messages";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { CloseDialogHandler } from "Infrastructure/Providers/DialogProvider/DialogProvider";
import { FormValue, areAllFieldsValid } from "Infrastructure/Utils/FormUtils";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { v4 as uuidv4 } from "uuid";
import { FeatureDeviceModel } from "Infrastructure/Utils/Nsure/FeatureUtils";
import { ActionType } from "Infrastructure/Api/ApiCustom";
import { getActionTypeDescription } from "Infrastructure/Utils/Nsure/DeviceUtils";
import ClearAllIcon from "@mui/icons-material/ClearAll";

export type FeatureUpdateFormData = {
	isValidationShown: boolean;
	code: FormValue<string>;
	isEnabled: FormValue<boolean>;
	isSelfInsEnabled: FormValue<boolean>;
	reason: FormValue<string>;
};

export const getNewFormData = () => {
	return {
		isValidationShown: false,
		code: {
			value: "",
		},
		isEnabled: {
			value: false,
			isValid: true,
		},
		isSelfInsEnabled: {
			value: false,
			isValid: true,
		},
		reason: {
			value: "",
			isValid: true,
		},
	};
};

type Props = {
	lacksSelfInsInstance?: boolean | null;
	deviceForm: DeviceForm;
	featureDeviceList: FeatureDeviceModel[] | null;
	hubConnection: HubConnection | null;
	closeDialog: CloseDialogHandler;
	onUpdate: (message: FeatureUpdateMessage) => void;
	formData: FeatureUpdateFormData;
	setFormData: (data: FeatureUpdateFormData) => void;
};

const FeatureActionUpdate: FunctionComponent<Props> = (props) => {
	const {
		lacksSelfInsInstance,
		deviceForm,
		featureDeviceList,
		hubConnection,
		closeDialog,
		onUpdate,
		formData,
		setFormData,
	} = props;

	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const handleSubmit = () => {
		if (isFormValid()) {
			if (
				!hubConnection ||
				hubConnection.state !== HubConnectionState.Connected
			) {
				showSnackbar(t(Resources.Errors.Hub.ConnectionError), "error");
				return;
			}

			const featureUpdateMessage: FeatureUpdateMessage = {
				requestId: uuidv4(),
				filteredDeviceNames: deviceForm.devices
					.filter((x) => x.isChecked)
					.map((x) => x.name),
				feature: {
					code: formData.code.value,
					isEnabled: formData.isEnabled.value ?? false,
					isSelfInsEnabled: formData.isSelfInsEnabled.value ?? false,
				},
				reason: formData.reason.value,
			};

			onUpdate(featureUpdateMessage);

			setFormData(getNewFormData());
		} else {
			setFormData({
				...formData,
				isValidationShown: true,
			});
		}
	};

	const handleCodeSelect = (event: any, selectedCode: string | null) => {
		if (!selectedCode) {
			setFormData(getNewFormData());
			return;
		}

		if (!featureDeviceList) return;

		const selectedFeature = featureDeviceList.find(
			(featureDevice) => featureDevice.feature.code === selectedCode
		);

		if (selectedFeature) {
			if (deviceForm.devices.length === 1) {
				setFormData({
					...formData,
					code: { value: selectedFeature.feature.code ?? "", isValid: true },
					isEnabled: {
						value: selectedFeature.feature.isEnabled,
						isValid: true,
					},
					isSelfInsEnabled: {
						value: selectedFeature.feature.isSelfInsEnabled,
						isValid: true,
					},
				});
			} else {
				setFormData({
					...formData,
					code: { value: selectedFeature.feature.code ?? "", isValid: true },
				});
			}
		}
	};

	const handleTextFieldChange = (e: any) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: { value: value, isValid: isFieldValid(name, value) },
		});
	};

	const handleCheckboxChange = (e: any, checked: boolean) => {
		const { name } = e.target;
		setFormData({
			...formData,
			[name]: { value: !!checked, isValid: true },
		});
	};

	const handleSelectChange = (e: any, _c: any) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: { value: value, isValid: isFieldValid(name, value) },
		});
	};

	const handleMultiSelectChange = (e: any, _c: any) => {
		const { name, value } = e.target;
		const newValues = typeof value === "string" ? value.split(",") : value;
		setFormData({
			...formData,
			[name]: {
				value: newValues,
				isValid: isFieldValid(name, newValues),
			},
		});
	};

	const isFieldValid = (name: string, value: any): boolean => {
		switch (name) {
			case "code":
				return !!value && value?.length > 0;
			default:
				return true;
		}
	};

	const isFormValid = () => areAllFieldsValid(formData);

	const getConfirmDialogContent = () => {
		const confirmContent = <>Aktualizovat feature?</>;
		return confirmContent;
	};

	const isFormDirty = () => {
		const initial = getNewFormData();
		return Object.keys(formData).some((key) => {
			const val = (formData as any)[key]?.value;
			const initVal = (initial as any)[key]?.value;
			if (Array.isArray(val) && Array.isArray(initVal)) {
				return val.join() !== initVal.join();
			}
			return val !== initVal;
		});
	};

	const formCanBeOpened = () => {
		if (deviceForm.devices.filter((x) => x.isChecked).length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return false;
		}
		return true;
	};

	return (
		<Card
			variant="outlined"
			elevation={0}
			sx={{ mt: 1 }}
			style={{ boxShadow: "none" }}
		>
			<CardHeader
				title={
					<Typography variant="subtitle1">
						{getActionTypeDescription(ActionType.FeatureUpdate)}
					</Typography>
				}
				action={
					<IconButton
						edge="end"
						size="small"
						onClick={() => setFormData(getNewFormData())}
						disabled={!isFormDirty()}
						sx={{ mr: 1 }}
					>
						<ClearAllIcon />
					</IconButton>
				}
			/>
			<Divider />
			<CardContent sx={{ pb: 0 }}>
				<Stack spacing={1}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Autocomplete
							options={
								featureDeviceList?.map((item) => item.feature.code) ?? []
							}
							getOptionLabel={(option: any) => option || ""}
							value={formData.code.value || ""}
							onChange={handleCodeSelect}
							sx={{ minWidth: "50%" }}
							renderInput={(params: any) => (
								<TextField
									{...params}
									name="code"
									label="FeatureCode"
									variant="outlined"
									size="small"
									error={formData.isValidationShown && !formData.code.isValid}
								/>
							)}
							freeSolo
						/>

						<FormControlLabel
							label="IsEnabled"
							control={
								<Checkbox
									name="isEnabled"
									checked={formData.isEnabled.value ?? false}
									onChange={handleCheckboxChange}
								/>
							}
						/>

						{lacksSelfInsInstance === null ||
							(lacksSelfInsInstance === false && (
								<FormControlLabel
									label="IsSelfInsEnabled"
									control={
										<Checkbox
											name="isSelfInsEnabled"
											checked={formData.isSelfInsEnabled.value ?? false}
											onChange={handleCheckboxChange}
										/>
									}
								/>
							))}
					</Stack>

					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						justifyContent="space-between"
					>
						<TextField
							name="reason"
							label="Důvod změny"
							variant="outlined"
							size="small"
							value={formData.reason.value}
							inputProps={{ maxLength: 200 }}
							onChange={handleTextFieldChange}
							error={formData.isValidationShown && !formData.reason.isValid}
							sx={{ flex: 1 }}
						/>
					</Stack>
				</Stack>
			</CardContent>
			<CardActions sx={{ justifyContent: "flex-end" }}>
				<BlConfirmButton
					modalTitle={"Aktualizace"}
					modalContent={getConfirmDialogContent()}
					onResult={(result) => {
						if (!!result) {
							handleSubmit();
						}
					}}
					disabled={!isFormValid()}
					onClick={() => formCanBeOpened()}
				>
					Aktualizovat
				</BlConfirmButton>
			</CardActions>
		</Card>
	);
};

export default FeatureActionUpdate;
